import React from "react";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import { useLocale, WithLocale } from "@insightfulscience/i18";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import HeroDescription from "@insightfulscience/atomic-react/Hero/Description";
import HeroAction from "@insightfulscience/atomic-react/Hero/Action";
import HeroButtonLink from "@insightfulscience/atomic-react/Hero/ButtonLink";
import Button from "@insightfulscience/atomic-react/Button";
import {
	ViewerPageTemplateSection,
	ViewerPageTemplateSectionSubTitle,
	ViewerPageTemplateSectionTitle,
	ViewerPageTemplateTablesSection,
	ViewerPageTemplateTableWrapper,
	ViewerPageTemplateTableSectionSubTitle,
	ViewerPageTemplateTableSectionText,
	ViewerPageTemplateTableSectionTitle,
} from "@insightfulscience/atomic-react/ViewerPageTemplate";
import TableResponsiveWrapper from "@insightfulscience/atomic-react/Table/ResponsiveWrapper";
import Table from "@insightfulscience/atomic-react/Table";
import Tr from "@insightfulscience/atomic-react/Table/Tr";
import Th from "@insightfulscience/atomic-react/Table/Th";
import Thead from "@insightfulscience/atomic-react/Table/Thead";
import Td from "@insightfulscience/atomic-react/Table/Td";
import Tbody from "@insightfulscience/atomic-react/Table/Tbody";
import Icon from "@insightfulscience/atomic-react/Icon";
import SimpleTemplate from "@insightfulscience/atomic-react/SimpleTemplate";
import SimpleTemplateTitle from "@insightfulscience/atomic-react/SimpleTemplate/Title";
import SimpleTemplateForm from "@insightfulscience/atomic-react/SimpleTemplate/Form";
import Paragraph from "@insightfulscience/atomic-react/Paragraph";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import ViewerForm from "@src/ViewerForm";
import SectionTryForFree from "@src/SectionTryForFree";
import WithServices from "@src/services";
import Link from "@insightfulscience/atomic-react/Link";
import NextLink from "@insightfulscience/smart-react/NextLink";
import resources from "../../locales/snapgene-viewer-page";
import SiteLayout from "../../src/SiteLayout";

const ViewerPage = () => {
	const [t] = useLocale("snapgene-viewer");
	return (
		<>
			<SiteLayout hero>
				<PageMeta title={t("PAGE_TITLE")} description={t("META_DESCRIPTION")}>
					<PageMeta.OGP
						title={t("PAGE_TITLE")}
						description={t("META_DESCRIPTION")}
						locale={t("META_LOCALE")}
						type={t("META_TYPE")}
						url={t("META_URL")}
						image={t("META_IMAGE")}
						imageSecureUrl={t("META_IMAGE_SECURE")}
						siteName={t("META_SITE_NAME")}
						articlePublisher={t("META_ARTICLE_PUBLISHER")}
						isImageBaseUrl
					/>
					<PageMeta.Twitter
						title={t("PAGE_TITLE")}
						description={t("META_DESCRIPTION")}
						site={t("META_TWITTER_SITE")}
						card={t("META_TWITTER_CARD")}
						image={t("META_TWITTER_IMAGE")}
						creator={t("META_TWITTER_CREATOR")}
						isImageBaseUrl
					/>
					<PageMeta.Canonical href={t("META_CANONICAL")} />
					<PageMeta.Author content={t("META_AUTHOR")} />
				</PageMeta>
				<Hero>
					<HeroTitle>{t("HERO_TITLE")}</HeroTitle>
					<HeroDescription sizing={{ maxW: 200 }} spacing={{ mr: "auto", ml: "auto" }}>
						{t("HERO_DESCRIPTION1")}
						<br />
						{t("HERO_DESCRIPTION2")}
					</HeroDescription>
					<HeroAction>
						<HeroButtonLink href={t("TRY_SNAPGENE_LINK")}>{t("TRY_SNAPGENE_BTN")}</HeroButtonLink>
					</HeroAction>
				</Hero>
				<SimpleTemplate utility={{ bgColor: "white" }} id="hubspotViewerForm">
					<SimpleTemplateTitle>{t("DOWNLOAD_SNAPGENE_TITLE")}</SimpleTemplateTitle>
					<SimpleTemplateForm>
						<ViewerForm />
					</SimpleTemplateForm>
					<Paragraph sx={{ fontSize: "xs", textAlign: "center" }}>
						{t("DOWNLOAD_SNAPGENE_SUBTEXT")}
					</Paragraph>
					<Paragraph sx={{ fontSize: "xs", textAlign: "center", fontColor: "black--40" }}>
						{t("DOWNLOAD_SNAPGENE_TEXT")}
						<NextLink href="/legal-disclaimers">{t("DOWNLOAD_SNAPGENE_TEXT_1")}</NextLink>
						{t("DOWNLOAD_SNAPGENE_TEXT_2")}
						<Link href="https://www.dotmatics.com/privacy-policy">
							{t("DOWNLOAD_SNAPGENE_TEXT_3")}
						</Link>
						.
					</Paragraph>
				</SimpleTemplate>
				<ViewerPageTemplateSection sm={12} md={12} lg={10}>
					<ViewerPageTemplateSectionTitle>
						{t("UNPARALLELED_VISIBILITY_TITLE")}
					</ViewerPageTemplateSectionTitle>
					<ViewerPageTemplateSectionSubTitle>
						{t("UNPARALLELED_VISIBILITY_DESCRIPTION")}
					</ViewerPageTemplateSectionSubTitle>
					<ViewerPageTemplateSectionSubTitle className="mt-20">
						{t("TABLE_TITLE")}
					</ViewerPageTemplateSectionSubTitle>
				</ViewerPageTemplateSection>
				<ViewerPageTemplateTablesSection>
					<ViewerPageTemplateTableWrapper>
						<TableResponsiveWrapper>
							<Table isBordered isStriped>
								<Thead>
									<Tr>
										<Th />
										<Th align="center" isUppercaseNone spacing={{ p: 2 }}>
											<ViewerPageTemplateTableSectionTitle>
												{t("TABLE_SECTION_TITLE1")}
											</ViewerPageTemplateTableSectionTitle>
											<ViewerPageTemplateTableSectionSubTitle>
												{t("TABLE_SECTION_SUBTITLE1_1")}
												<br /> {t("TABLE_SECTION_SUBTITLE1_2")}
											</ViewerPageTemplateTableSectionSubTitle>
											<Button.Link href="#hubspotViewerForm" size="s">
												{t("DOWNLOAD_NOW_BTN")}
											</Button.Link>
										</Th>
										<Th align="center" isUppercaseNone spacing={{ p: 2 }}>
											<ViewerPageTemplateTableSectionTitle>
												{t("TABLE_SECTION_TITLE2")}
											</ViewerPageTemplateTableSectionTitle>
											<ViewerPageTemplateTableSectionSubTitle>
												{t("TABLE_SECTION_SUBTITLE2_1")}
												<br /> {t("TABLE_SECTION_SUBTITLE2_2")}
											</ViewerPageTemplateTableSectionSubTitle>
											<Button.Link
												href={t("TRY_FOR_FREE_LINK")}
												size="s"
												isOutline2
												styleType="secondary"
											>
												{t("TRY_FOR_FREE_BTN")}
											</Button.Link>
										</Th>
									</Tr>
								</Thead>
								<Tbody>
									<Tr>
										<Td>
											<ViewerPageTemplateTableSectionText>
												{t("TABLE_ROW1_TEXT")}
											</ViewerPageTemplateTableSectionText>
										</Td>
										<Td sx={{ textAlign: "center" }}>
											<Icon name="check" />
										</Td>
										<Td sx={{ textAlign: "center" }}>
											<Icon name="check" />
										</Td>
									</Tr>
									<Tr>
										<Td>
											<ViewerPageTemplateTableSectionText>
												{t("TABLE_ROW2_TEXT")}
											</ViewerPageTemplateTableSectionText>{" "}
										</Td>
										<Td sx={{ textAlign: "center" }}>
											<Icon name="check" />
										</Td>
										<Td sx={{ textAlign: "center" }}>
											<Icon name="check" />
										</Td>
									</Tr>
									<Tr>
										<Td>
											<ViewerPageTemplateTableSectionText>
												{t("TABLE_ROW3_TEXT")}
											</ViewerPageTemplateTableSectionText>
										</Td>
										<Td sx={{ textAlign: "center" }}>
											<Icon name="check" />
										</Td>
										<Td sx={{ textAlign: "center" }}>
											<Icon name="check" />
										</Td>
									</Tr>
									<Tr>
										<Td>
											<ViewerPageTemplateTableSectionText>
												{t("TABLE_ROW4_TEXT")}
											</ViewerPageTemplateTableSectionText>
										</Td>
										<Td sx={{ textAlign: "center" }}>
											<Icon name="times" />
										</Td>
										<Td sx={{ textAlign: "center" }}>
											<Icon name="check" />
										</Td>
									</Tr>
									<Tr>
										<Td>
											<ViewerPageTemplateTableSectionText>
												{t("TABLE_ROW5_TEXT")}
											</ViewerPageTemplateTableSectionText>
										</Td>
										<Td sx={{ textAlign: "center" }}>
											<Icon name="times" />
										</Td>
										<Td sx={{ textAlign: "center" }}>
											<Icon name="check" />
										</Td>
									</Tr>
									<Tr>
										<Td>
											<ViewerPageTemplateTableSectionText>
												{t("TABLE_ROW6_TEXT")}
											</ViewerPageTemplateTableSectionText>
										</Td>
										<Td sx={{ textAlign: "center" }}>
											<Icon name="times" />
										</Td>
										<Td sx={{ textAlign: "center" }}>
											<Icon name="check" />
										</Td>
									</Tr>
									<Tr>
										<Td>
											<ViewerPageTemplateTableSectionText>
												{t("TABLE_ROW7_TEXT")}
											</ViewerPageTemplateTableSectionText>
										</Td>
										<Td sx={{ textAlign: "center" }}>
											<Icon name="times" />
										</Td>
										<Td sx={{ textAlign: "center" }}>
											<Icon name="check" />
										</Td>
									</Tr>
								</Tbody>
							</Table>
						</TableResponsiveWrapper>
					</ViewerPageTemplateTableWrapper>
				</ViewerPageTemplateTablesSection>
				<SectionTryForFree />
			</SiteLayout>
		</>
	);
};

export default pipe(ViewerPage, WithLocale({ resources }), WithServices);
